import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { useTranslations } from "@/lib/i18n/intl";
import { Coachmark, type TourStep } from "@/shared/components/Coachmark";
import { resolvePageHelp, visibleSteps } from "@/shared/help/page-help";
import {
  PAGE_HELP_CHANGED,
  isPageHelpEnabled,
} from "@/shared/help/help-preference";
import { runPageTour, usePageTourAvailable } from "@/shared/help/page-tour";
import { useShowingSampleData } from "@/shared/help/sample-data";

const QuestionIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9.2 9a2.8 2.8 0 1 1 3.8 2.6c-.7.3-1 .9-1 1.6v.3" />
    <path d="M12 17.2v.1" />
  </svg>
);

/**
 * Floating "explain this page" button.
 *
 * Sits where the setup helper sits and is mounted beside it, but the two never
 * show together: `ProtectedLayout` renders this one only once setup is done, so
 * the corner holds "finish setting up" first and "what is this page?" after —
 * one floating affordance, whose meaning changes when the seller's need does.
 *
 * Mobile-first: the same 12x12 target the setup FAB uses, clear of the bottom
 * tab bar and the safe area, and the tour it opens is `Coachmark`, whose card
 * is clamped to the viewport and placed vertically (never left/right, which
 * does not fit 375px).
 */
export function HelpLauncher({
  storeSlug,
  open,
  onOpenChange,
  onAnchorChange,
}: {
  storeSlug: string;
  /** Owned by the shell so the page tree can render sample data while a tour
   *  runs — see `HelpDemoProvider`. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Reports the step the tour is on, so pages can follow along. */
  onAnchorChange: (anchor: string | null) => void;
}) {
  const t = useTranslations();
  const { pathname } = useLocation();
  const [enabled, setEnabled] = useState(() => isPageHelpEnabled(storeSlug));
  const setOpen = onOpenChange;

  // The Settings toggle lives in another tree; both events cover both cases —
  // `storage` for other tabs, the custom one for this tab.
  useEffect(() => {
    const sync = () => setEnabled(isPageHelpEnabled(storeSlug));
    window.addEventListener(PAGE_HELP_CHANGED, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(PAGE_HELP_CHANGED, sync);
      window.removeEventListener("storage", sync);
    };
  }, [storeSlug]);

  // Leaving the page ends its explanation — the steps describe the screen the
  // seller was on, not the one they just opened.
  useEffect(() => setOpen(false), [pathname, setOpen]);

  const pageHelp = useMemo(() => resolvePageHelp(pathname), [pathname]);
  const allSteps: TourStep[] = useMemo(
    () => (pageHelp ? pageHelp.steps((key) => t(key)) : []),
    [pageHelp, t],
  );
  /**
   * Only the steps this screen can actually show. A page's regions differ by
   * breakpoint — the customers list and profile pane are the desktop split
   * view; the phone renders one full-bleed screen instead — so a fixed list
   * promised "step 1 of 3" and then skipped straight to the end. Resolved when
   * the tour OPENS, against the DOM in front of the seller.
   */
  const [steps, setSteps] = useState<TourStep[]>([]);
  const openTour = useCallback(() => setOpen(true), [setOpen]);
  /**
   * Filtered AFTER opening, not at the click. Opening the tour is what makes
   * empty regions render their sample rows, and those rows are what create the
   * anchors — filtering first counted the pre-sample DOM and offered one step
   * where the page was about to have two. A layout effect sees the same commit
   * the samples landed in.
   */
  // `allSteps` is read through a ref, and the effect keys on `open` ALONE.
  // Depending on the array itself spun: `t` from `useTranslations` is a new
  // function every render, so the memo behind it changes identity every render,
  // so the effect re-ran, called setSteps with a fresh array, re-rendered, and
  // went round again until React gave up ("Maximum update depth exceeded").
  const allStepsRef = useRef(allSteps);
  useLayoutEffect(() => {
    allStepsRef.current = allSteps;
  }, [allSteps]);
  useLayoutEffect(() => {
    if (!open) return;
    const resolve = () =>
      setSteps(
        visibleSteps(
          allStepsRef.current,
          (anchor) => document.querySelector(`[data-tour="${anchor}"]`) !== null,
        ),
      );
    resolve();
    // …and once more a beat later. Opening the tour is what makes empty regions
    // render samples and what opens the sample profile, and those land in a
    // LATER commit than this effect — counting only the first pass offered two
    // steps on a screen that was about to have three.
    const settle = window.setTimeout(resolve, 350);
    return () => window.clearTimeout(settle);
  }, [open]);
  // Some screens have a deeper walkthrough of their own (orders has the
  // guided sample-order tour). Where one exists, the last step stops saying
  // "Done" and offers it instead — page help explains the screen, and then
  // hands over to the thing that teaches the work.
  const hasPageTour = usePageTourAvailable();
  const showingSamples = useShowingSampleData();
  /**
   * "You just switched this on — press it." Settings sends the seller here
   * (`?helper=try`) the moment they enable the helper, because a toggle two
   * screens away teaches nothing about a round button in a corner.
   *
   * A hand on the button and no Next: the press IS the lesson, and it opens
   * the page's own tour, which is the answer to "what does this do?".
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const inviting = searchParams.get("helper") === "try" && !open;
  const clearInvite = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("helper");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const inviteSteps: TourStep[] = useMemo(
    () => [
      {
        anchor: "page-help-fab",
        title: t("coachmark.helpTryTitle"),
        body: t("coachmark.helpTryBody"),
        awaitClick: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `t` is a new function each render
    [],
  );
  const close = useCallback(() => {
    setOpen(false);
    onAnchorChange(null);
  }, [setOpen, onAnchorChange]);

  // No help written for this screen yet → no button. Better than a button that
  // apologises for having nothing to say.
  if (!enabled || !pageHelp || allSteps.length === 0) return null;

  return (
    <>
      {inviting ? (
        <Coachmark steps={inviteSteps} onClose={clearInvite} />
      ) : null}
      {open && steps.length > 0 ? (
        <Coachmark
          steps={steps}
          onClose={close}
          onProceed={
            hasPageTour
              ? () => {
                  close();
                  runPageTour();
                }
              : undefined
          }
          proceedLabel={hasPageTour ? t("pageHelp.runTour") : undefined}
          sampleNotice={showingSamples ? t("pageHelp.sampleNotice") : undefined}
          onStepChange={onAnchorChange}
        />
      ) : null}
      <button
        type="button"
        onClick={openTour}
        // Pointed at once, by the setup step that switches this on: the
        // seller flips a toggle in Settings and the button appears in a
        // corner they were not looking at.
        data-tour="page-help-fab"
        aria-label={t("pageHelp.launcher")}
        className="fixed bottom-[calc(6.5rem+env(safe-area-inset-bottom))] end-4 z-30 flex h-12 w-12 items-center justify-center rounded-full border border-[color:var(--border-strong)] bg-[color:var(--surface)] text-[color:var(--text-primary)] shadow-lg transition-colors hover:bg-[color:var(--surface-alt)] lg:bottom-6"
      >
        <QuestionIcon />
      </button>
    </>
  );
}
